<template>
    <v-container fluid>
        <AddOrderNumberDialog
            :is-open="addOrderNumberDialog"
            @on-confirm="saveOrderNumber"
            @on-close="closeOrderNumberDialog"
        />
        <OrderQualitativeNotesDialog
            :is-open="orderQualitativeNotesDialog"
            :qualitative-notes="qualitativeNotes"
            @on-close="closeOrderQualitativeNotesDialog"
        />
        <v-container>
            <v-row>
                <v-col v-if="action === 'updateComplaint'" cols="12">
                    <p class="text-subtitle-1">
                        <strong>Koszty zamówień:</strong> {{ complaint.nettoPrice }}
                    </p>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12" lg="3">
                    <v-select
                        v-model="complaint.complaintTypeId"
                        :items="complaintTypes"
                        item-value="id"
                        no-data-text="Nie znaleziono typu reklamacji"
                        :error-messages="complaintTypeIdErrors()"
                        label="Typ reklamacji"
                        required
                        :disabled="!canSaveComplaint || isComplaintIneditable"
                        @change="complaintTypeChange"
                    >
                        <template slot="selection" slot-scope="data">
                            {{ data.item.name }}
                        </template>

                        <template slot="item" slot-scope="data">
                            {{ data.item.name }}
                        </template>
                    </v-select>
                </v-col>
                <v-col cols="12" lg="3">
                    <v-select
                        v-model="complaint.complaintProductionDepartmentId"
                        :items="productionDepartments"
                        label="Dział"
                        item-value="id"
                        no-data-text="Nie znaleziono działu"
                        :disabled="disableFieldsOnExternalCreation || !canSaveComplaint || isComplaintIneditable"
                        @change="changeProductionDepartment"
                    >
                        <template slot="selection" slot-scope="data">
                            {{ data.item.name }}
                        </template>

                        <template slot="item" slot-scope="data">
                            {{ data.item.name }}
                        </template>
                    </v-select>
                </v-col>
                <v-col cols="12" lg="3">
                    <v-select
                        v-model="complaint.complaintProductionSubDepartmentId"
                        :items="productionSubDepartments"
                        label="Wydział"
                        item-value="id"
                        no-data-text="Nie znaleziono wydziału"
                        :disabled="disableFieldsOnExternalCreation || !canSaveComplaint || isComplaintIneditable"
                    >
                        <template slot="selection" slot-scope="data">
                            {{ data.item.name }}
                        </template>

                        <template slot="item" slot-scope="data">
                            {{ data.item.name }}
                        </template>
                    </v-select>
                </v-col>
                <v-col cols="12" lg="3">
                    <v-autocomplete
                        v-model="complaint.machineId"
                        :items="machines"
                        label="Maszyna"
                        :filter="customMachinesFilter"
                        item-value="id"
                        no-data-text="Nie znaleziono symbolu maszyny"
                        :disabled="disableFieldsOnExternalCreation || !canSaveComplaint || isComplaintIneditable"
                        class="v-text-field--placeholder-dark"
                        clearable
                    >
                        <template slot="selection" slot-scope="data">
                            {{ data.item.symbol }}
                        </template>
                        <template slot="item" slot-scope="data">
                            {{ data.item.symbol }}
                        </template>
                    </v-autocomplete>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12" lg="8">
                    <v-autocomplete
                        v-model="selectedOrdersProductionNumbers"
                        :items="ordersProductionNumbers"
                        :search-input.sync="ordersProductionNumbersSearch"
                        :filter="customOrdersProductionNumbersFilter"
                        :loading="ordersProductionNumbersLoading"
                        :error-messages="complaintOrderNumberErrors()"
                        class="v-text-field--placeholder-dark"
                        label="Numer zamówienia"
                        required
                        dense
                        multiple
                        chips
                        deletable-chips
                        cache-items
                        item-text="productionNumber"
                        item-value="productionNumber"
                        no-data-text="Nie znaleziono"
                        clearable
                        :disabled="!canSaveComplaint || isComplaintIneditable"
                        @input="selectedOrdersProductionNumbersUpdate"
                        @change="ordersProductionNumbersSearch = null"
                    >
                        <template slot="no-data">
                            <v-list-item>
                                <v-list-item-title>
                                    <template v-if="!ordersProductionNumbersSearch">
                                        Wpisz treść aby odnaleźć <strong>zamówienie</strong>.
                                    </template>
                                    <template v-else-if="ordersProductionNumbersSearch.length < ordersProductionNumbersSearchMinQueryLength">
                                        Wpisz przynajmniej <strong>{{ ordersProductionNumbersSearchMinQueryLength }}</strong> znaki.
                                    </template>
                                    <template v-else-if="ordersProductionNumbersLoading">
                                        Trwa wyszukiwanie zamówień...
                                    </template>
                                    <template v-else>
                                        Nie odnaleziono zamówień na zapytanie: "<strong> {{ ordersProductionNumbersSearch }}".</strong>
                                    </template>
                                </v-list-item-title>
                            </v-list-item>
                        </template>
                        <template #selection="data">
                            <v-chip
                                v-bind="data.attrs"
                                close
                                @click="data.select"
                                @click:close="removeProductionNumber(data.item)"
                            >
                                <v-btn
                                    v-if="hasOrders(data.item)"
                                    icon
                                    small
                                    data-sentry-label="Pokaż uwagi jakościowe"
                                    @click="showOrderQualitativeNotesDialog(data.item)"
                                >
                                    <v-icon color="error">
                                        mdi-exclamation
                                    </v-icon>
                                </v-btn>
                                {{ data.item }}
                            </v-chip>
                        </template>
                        <template slot="append-outer">
                            <v-tooltip :open-delay="tooltipDelay" top>
                                <template #activator="{on}">
                                    <v-btn
                                        icon
                                        data-sentry-label="Dodaj numer zamówienia"
                                        v-on="on"
                                        @click="showAddOrderNumberDialog"
                                    >
                                        <v-icon color="primary">
                                            mdi-plus
                                        </v-icon>
                                    </v-btn>
                                </template>
                                <span>Dodaj numer zamówienia</span>
                            </v-tooltip>
                        </template>
                    </v-autocomplete>
                </v-col>
                <v-col cols="12" lg="4">
                    <v-checkbox
                        v-model="complaint.priority"
                        :disabled="isPriorityLimitReached || !canSaveComplaint || isComplaintIneditable"
                        label="Priorytet"
                    />
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12" lg="6">
                    <v-autocomplete
                        v-model="complaint.discrepancyCatcherId"
                        :items="users"
                        label="Kto wyłapał błąd"
                        :filter="customUsersFilter"
                        :loading="usersSearchLoading"
                        :search-input.sync="usersSearch"
                        class="v-text-field--placeholder-dark"
                        dense
                        :disabled="disableFieldsOnExternalCreation || !canSaveComplaint || isComplaintIneditable"
                        item-value="id"
                        no-data-text="Nie znaleziono"
                        clearable
                    >
                        <template slot="no-data">
                            <v-list-item>
                                <v-list-item-title>
                                    <template v-if="!usersSearch">
                                        Wpisz treść aby odnaleźć <strong>osobę, która wyłapała błąd</strong>.
                                    </template>
                                    <template v-else-if="usersSearchLoading">
                                        Trwa wyszukiwanie...
                                    </template>
                                    <template v-else>
                                        Nie znaleziono
                                    </template>
                                </v-list-item-title>
                            </v-list-item>
                        </template>
                        <template slot="selection" slot-scope="data">
                            <template v-if="data.item.firstName && data.item.lastName">
                                {{ data.item.firstName }} {{ data.item.lastName }}
                            </template>
                            <template v-else>
                                {{ data.item.username }}
                            </template>
                        </template>
                        <template slot="item" slot-scope="data">
                            <template v-if="data.item.firstName && data.item.lastName">
                                {{ data.item.firstName }} {{ data.item.lastName }}
                            </template>
                            <template v-else>
                                {{ data.item.username }}
                            </template>
                        </template>
                    </v-autocomplete>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12">
                    <v-textarea
                        v-model="complaint.complaintProductDescription"
                        name="Reklamowana część"
                        label="Opis reklamowanej części"
                        :disabled="!canSaveComplaint || isComplaintIneditable"
                    />
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12">
                    <v-textarea
                        v-model="complaint.discrepancyDescription"
                        name="Opis"
                        :error-messages="complaintDiscrepancyDescriptionErrors()"
                        label="Opis niezgodności / oczekiwania klienta"
                        required
                        :disabled="!canSaveComplaint || isComplaintIneditable"
                    />
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12" lg="4">
                    <v-text-field
                        v-model="complaint.scaleAmount"
                        :error-messages="complaintScaleAmountErrors()"
                        label="Skala niezgodności"
                        suffix="szt."
                        required
                        :disabled="!canSaveComplaint || isComplaintIneditable"
                    />
                </v-col>
                <v-col cols="12" lg="4">
                    <v-text-field
                        v-model="complaint.scalePercent"
                        :error-messages="complaintScalePercentErrors()"
                        label="Skala niezgodności"
                        suffix="%"
                        required
                        :disabled="!canSaveComplaint || isComplaintIneditable"
                    />
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12" lg="3">
                    <v-select
                        v-if="action === 'updateComplaint'"
                        v-model="complaint.complaintStatusId"
                        :items="filteredStatuses"
                        item-value="id"
                        label="Status"
                        :disabled="!canSaveComplaint || isDraftComplaint"
                    >
                        <template slot="selection" slot-scope="data">
                            {{ data.item.name }}
                        </template>

                        <template slot="item" slot-scope="data">
                            {{ data.item.name }}
                        </template>
                    </v-select>
                </v-col>
            </v-row>
        </v-container>
    </v-container>
</template>

<script>
import AddOrderNumberDialog from './AddOrderNumberDialog'
import OrderQualitativeNotesDialog from './OrderQualitativeNotesDialog'
import customFilters from '@/mixins/customFilters'
import tooltipDelay from '@/mixins/tooltip'
import { errorHandler, addSentryBreadcrumb } from '@/utils/errorHandler'
import { EventBus } from '@/utils/eventBus'

let searchTimer

export default {
    name: 'Details',
    components: {
        AddOrderNumberDialog,
        OrderQualitativeNotesDialog
    },
    mixins: [ customFilters, tooltipDelay ],
    props: {
        complaint: {
            type: Object,
            default: null
        },
        isInternal: {
            type: Boolean
        },
        validations: {
            type: Object,
            default: null
        },
        action: {
            type: String,
            default: null
        },
        isPriorityLimitReached: {
            type: Boolean
        },
        canSaveComplaint: {
            type: Boolean
        }
    },
    data() {
        return {
            ordersProductionNumbersLoading: false,
            ordersProductionNumbersSearch: this.$parent.$attrs.order,
            ordersProductionNumbersSearchMinQueryLength: 2,
            ordersProductionNumber: null,
            selectedOrdersProductionNumbers: this.$store.state.complaints.complaints.orderNumbers,
            addOrderNumberDialog: false,
            usersSearch: null,
            usersSearchLoading: false,
            users: [],
            productionSubDepartments: [],
            orderQualitativeNotesDialog: false,
            qualitativeNotes: []
        }
    },
    computed: {
        userId() {
            return this.$store.state.user.userId
        },
        isDraftComplaint() {
            return this.complaint.complaintStatusId === 15 // draft
        },
        isComplaintIneditable() {
            return this.isDraftComplaint && this.complaint.creator.id !== this.userId
        },
        ordersProductionNumbers() {
            return this.$store.state.complaints.complaints.productionNumbersList
        },
        complaintTypes() {
            return this.$store.state.complaints.complaints.complaintTypes
        },
        complaintStatuses() {
            return this.$store.state.complaints.complaints.complaintStatuses
        },
        filteredStatuses() {
            if (!this.isDraftComplaint) {
                return this.complaintStatuses.filter(status => status.id !== 15) // bez statusu robocza
            }

            return this.complaintStatuses
        },
        productionDepartments() {
            return this.$store.state.complaints.complaints.productionDepartments
        },
        disableFieldsOnExternalCreation() {
            return !this.isInternal && this.action != 'updateComplaint'
        },
        machines() {
            return this.$store.state.configurations.machines.machines
        },
        orderNumbers() {
            return this.$store.state.complaints.complaints.orderNumbers
        }
    },
    watch: {
        ordersProductionNumbersSearch(value = null) {
            if (value === null || value.length < this.ordersProductionNumbersSearchMinQueryLength) return

            this.ordersProductionNumbersLoading = true

            clearTimeout(searchTimer)

            searchTimer = setTimeout(() => {
                if (value) {
                    this.searchProductionNumbers(value)
                } else {
                    this.ordersProductionNumbersLoading = false
                }
            }, 500)
        },
        usersSearch(value = null) {
            if (value === null) return

            this.usersSearchLoading = true

            clearTimeout(searchTimer)

            searchTimer = setTimeout(() => {
                if (value) {
                    this.searchUsers(value)
                } else {
                    this.usersSearchLoading = false
                    this.users = []
                }
            }, 500)
        },
    },
    mounted() {
        this.productionSubDepartments = this.complaint.complaintProductionDepartment ? this.complaint.complaintProductionDepartment.productionSubDepartments : []
        this.complaint.discrepancyCatcher ? this.users.push(this.complaint.discrepancyCatcher) : []

        EventBus.$on('selectedOrderNumberManual', () => {
            this.selectedOrdersProductionNumbers = this.orderNumbers
            this.$root.$emit('selectedOrderNumber', this.selectedOrdersProductionNumbers)
        })
        EventBus.$on('selectedComplaintTypeManual', this.complaintTypeChange)
        EventBus.$on('selectedProductionDepartmentManual', this.changeProductionDepartment)
    },
    methods: {
        changeProductionDepartment() {
            let department = this.productionDepartments.find(item => item.id === this.complaint.complaintProductionDepartmentId)

            this.complaint.complaintProductionSubDepartmentId = null
            this.productionSubDepartments = department?.productionSubDepartments
        },
        showAddOrderNumberDialog() {
            this.addOrderNumberDialog = true
        },
        saveOrderNumber(orderNumber) {
            if (!this.selectedOrdersProductionNumbers.includes(orderNumber)) {
                this.selectedOrdersProductionNumbers.push(orderNumber)
                this.$store.dispatch('complaints/setProductionNumbersList', this.selectedOrdersProductionNumbers)
            }
            this.selectedOrdersProductionNumbersUpdate()
        },
        closeOrderNumberDialog() {
            this.addOrderNumberDialog = false
        },
        complaintTypeChange() {
            this.$root.$emit('selectedComplaintTypeId', this.complaint.complaintTypeId)
            this.complaint.complaintProductionDepartmentId = null
            this.complaint.machineId = null
            this.complaint.discrepancyCatcherId = null
        },
        selectedOrdersProductionNumbersUpdate() {
            const existingOrderNumbers = new Set(this.complaint.complaintOrderComplaint.map(complaint => complaint.orderNumber))
    
            const newOrderComplaints = this.selectedOrdersProductionNumbers
                .filter(orderNumber => !existingOrderNumbers.has(orderNumber))
                .map(orderNumber => ({ orderNumber }))
            
            this.complaint.complaintOrderComplaint.push(...newOrderComplaints)
            this.complaint.complaintOrderComplaint = this.complaint.complaintOrderComplaint.filter(
                complaint => this.selectedOrdersProductionNumbers.includes(complaint.orderNumber)
            )
        },
        searchProductionNumbers(query = '') {
            addSentryBreadcrumb('ui.action', 'Wyszukiwanie numeru zamówienia')

            this.$store.dispatch('complaints/findOrder', {
                query: query,
                selectedOrdersProductionNumbers: this.selectedOrdersProductionNumbers
            })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.ordersProductionNumbersLoading = false
                })
        },
        searchUsers(query = '') {
            addSentryBreadcrumb('ui.action', 'Wyszukiwanie użytkownika')

            this.$store.dispatch('administration/findUsers', {
                paginate: '?limit=20',
                query: query
            })
                .then(response => {
                    this.users = response.data.results
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.usersSearchLoading = false
                })
        },
        removeProductionNumber(productionNumber) {
            this.selectedOrdersProductionNumbers.splice(this.selectedOrdersProductionNumbers.indexOf(productionNumber), 1)
            this.selectedOrdersProductionNumbersUpdate()
            this.$store.dispatch('complaints/setProductionNumbersList', this.selectedOrdersProductionNumbers)
        },
        customOrdersProductionNumbersFilter(item, queryText) {
            const ordersProductionNumber = item.toLowerCase(),
                search = queryText.toLowerCase()

            return ordersProductionNumber.indexOf(search) > -1
        },
        customMachinesFilter(item, queryText) {
            const symbol = item.symbol.toLowerCase(),
                search = queryText.toLowerCase()

            return symbol.indexOf(search) > -1
        },
        hasOrders(order) {
            let orderNumber = order.split(' - ')[0].replace(/\s/g, '')

            if (this.complaint.complaintQualitativeNotes) {
                return this.complaint.complaintQualitativeNotes.find(item => item.orderNumber == orderNumber)?.qualitativeNotes.length > 0
            }
        },
        showOrderQualitativeNotesDialog(order) {
            let orderNumber = order.split(' - ')[0].replace(/\s/g, '')

            let complaintQualitativeNote = this.complaint.complaintQualitativeNotes.find(item => item.orderNumber == orderNumber)

            this.qualitativeNotes = complaintQualitativeNote === undefined ? [] : complaintQualitativeNote.qualitativeNotes
            this.orderQualitativeNotesDialog = true
        },
        closeOrderQualitativeNotesDialog() {
            this.orderQualitativeNotesDialog = false
        },
        //Walidacje
        complaintTypeIdErrors() {
            const errors = []

            if (!this.validations.complaint.complaintTypeId.$dirty) return errors
            !this.validations.complaint.complaintTypeId.required && errors.push('Typ reklamacji jest wymagany.')

            return errors
        },
        complaintOrderNumberErrors() {
            const errors = []

            if (!this.validations.complaint.complaintOrderComplaint.$dirty) return errors

            if (this.validations.complaint.complaintOrderComplaint.$error) {
                errors.push('Numer zamówienia jest wymagany.')
            }

            return errors
        },
        complaintDiscrepancyDescriptionErrors() {
            const errors = []

            if (!this.validations.complaint.discrepancyDescription.$dirty) return errors
            !this.validations.complaint.discrepancyDescription.required && errors.push('Opis niezgodności jest wymagany.')

            return errors
        },
        complaintScaleAmountErrors() {
            const errors = []

            if (!this.validations.complaint.scaleAmount.$dirty) return errors
            !this.validations.complaint.scaleAmount.required && errors.push('Skala niezgodności jest wymagana.')
            !this.validations.complaint.scaleAmount.integer && errors.push('Proszę wpisać liczbę całkowitą.')

            return errors
        },
        complaintScalePercentErrors() {
            const errors = []

            if (!this.validations.complaint.scalePercent.$dirty) return errors
            !this.validations.complaint.scalePercent.required && errors.push('Skala niezgodności jest wymagana.')
            !this.validations.complaint.scalePercent.integer && errors.push('Proszę wpisać liczbę całkowitą.')

            return errors
        },
        complaintStatus() {
            const errors = []

            if (!this.validations.complaint.complaintStatusId.$dirty) return errors

            return errors
        },

    },
}
</script>